import type { FormHTMLAttributes } from "react";
import { useEffect, useState } from "react";
import type { RaRecord } from "ra-core";
import {
  FilterLiveForm,
  useDataProvider,
  useGetOne,
  useNotify,
  useRecordContext,
  useRefresh,
} from "ra-core";
import { useParams } from "react-router-dom";
import { Filter, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ColumnsButton,
  DataTable,
  ListPagination,
  ReferenceManyField,
  SearchInput,
  TextField,
} from "@/components/admin";
import { DateAgo } from "@/common/DateAgo";
import { SelectionList } from "@/components/SelectionList";

/**
 * Shared implementation of the role and user "Permissions" edit tabs, which
 * are identical apart from the API resource, the reference target, and (for
 * users) an optional organization scope. Each resource keeps a thin wrapper
 * that fixes those via <PermissionsTab>.
 */
export interface PermissionsTabProps {
  /** API resource segment used for create/delete: `roles` or `users`. */
  resource: "roles" | "users";
  /** ReferenceManyField target linking a permission back to the subject. */
  target: "role_id" | "user_id";
  /** Subject noun for the "already has all available scopes" message. */
  subjectNoun: "role" | "user";
  /**
   * When set, permissions can be scoped to an organization (users only): adds
   * the organization picker, an Organization column, and an `organization_id`
   * on each granted permission.
   */
  withOrgScope?: boolean;
}

interface ResourceServer extends RaRecord {
  identifier: string;
  name?: string;
  scopes?: Array<{
    value?: string;
    permission_name?: string;
    description?: string;
  }>;
}

interface Organization extends RaRecord {
  name?: string;
  display_name?: string;
}

interface PermissionRecord extends RaRecord {
  permission_name: string;
  resource_server_identifier: string;
  resource_server_name?: string;
  description?: string;
  created_at?: string;
  organization_id?: string;
}

// Sentinel Select value for "no organization" (tenant-global permission),
// since a shadcn SelectItem cannot use an empty string as its value.
const NO_ORG = "__none__";

function AddPermissionButton({
  resource,
  target,
  subjectNoun,
  withOrgScope,
}: PermissionsTabProps) {
  const { id: subjectId } = useParams();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [resourceServers, setResourceServers] = useState<ResourceServer[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string>("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgsLoadFailed, setOrgsLoadFailed] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(NO_ORG);
  const [available, setAvailable] = useState<
    Array<{ permission_name: string; description: string }>
  >([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const selectedServer = resourceServers.find(
    (s) => s.identifier === selectedServerId,
  );

  const reset = () => {
    setSelectedServerId("");
    setSelectedOrgId(NO_ORG);
    setAvailable([]);
    setSelected(new Set());
    setOrgsLoadFailed(false);
  };

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setOrgsLoadFailed(false);
    try {
      const serversPromise = dataProvider.getList<ResourceServer>(
        "resource-servers",
        {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "name", order: "ASC" },
          filter: {},
        },
      );
      if (withOrgScope) {
        const [servers, orgs] = await Promise.all([
          serversPromise,
          dataProvider
            .getList<Organization>("organizations", {
              pagination: { page: 1, perPage: 100 },
              sort: { field: "display_name", order: "ASC" },
              filter: {},
            })
            // Surface the failure rather than silently degrading to tenant-wide
            // grants — an admin might add a tenant-wide permission when they
            // meant an organization-scoped one.
            .catch(() => {
              setOrgsLoadFailed(true);
              notify("Error loading organizations", { type: "error" });
              return { data: [] as Organization[] };
            }),
        ]);
        setResourceServers(servers.data);
        setOrganizations(orgs.data);
      } else {
        const servers = await serversPromise;
        setResourceServers(servers.data);
      }
    } catch {
      notify("Error loading resource servers", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  useEffect(() => {
    if (!selectedServerId || !subjectId) return;
    const server = resourceServers.find(
      (s) => s.identifier === selectedServerId,
    );
    if (!server) return;
    const orgId = selectedOrgId === NO_ORG ? undefined : selectedOrgId;
    setSelected(new Set());
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const allScopes = (server.scopes ?? []).map((s) => ({
          permission_name: s.permission_name ?? s.value ?? "",
          description: s.description ?? "",
        }));
        // roles read via the reference; users read the flat sub-resource.
        const existing =
          resource === "roles"
            ? await dataProvider.getManyReference<PermissionRecord>(
                "permissions",
                {
                  target,
                  id: subjectId,
                  pagination: { page: 1, perPage: 200 },
                  sort: { field: "permission_name", order: "ASC" },
                  filter: {},
                },
              )
            : await dataProvider.getList<PermissionRecord>(
                `${resource}/${subjectId}/permissions`,
                {
                  pagination: { page: 1, perPage: 200 },
                  sort: { field: "permission_name", order: "ASC" },
                  filter: {},
                },
              );
        if (cancelled) return;
        // A permission is unique per (resource server, permission,
        // organization), so only exclude scopes already assigned for the
        // selected organization.
        const existingNames = new Set(
          existing.data
            .filter(
              (p) =>
                p.resource_server_identifier === server.identifier &&
                (!withOrgScope || (p.organization_id || undefined) === orgId),
            )
            .map((p) => p.permission_name),
        );
        setAvailable(
          allScopes.filter(
            (s) => s.permission_name && !existingNames.has(s.permission_name),
          ),
        );
      } catch {
        if (!cancelled) notify("Error loading permissions", { type: "error" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    selectedServerId,
    selectedOrgId,
    subjectId,
    resourceServers,
    resource,
    target,
    withOrgScope,
    dataProvider,
    notify,
  ]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleAdd = async () => {
    if (!subjectId || !selectedServer || selected.size === 0) return;
    const orgId = selectedOrgId === NO_ORG ? undefined : selectedOrgId;
    try {
      await dataProvider.create(`${resource}/${subjectId}/permissions`, {
        data: {
          permissions: Array.from(selected).map((permission_name) => ({
            permission_name,
            resource_server_identifier: selectedServer.identifier,
            ...(withOrgScope && orgId ? { organization_id: orgId } : {}),
          })),
        },
      });
      notify(`${selected.size} permission(s) added`, { type: "success" });
      handleClose();
      refresh();
    } catch {
      notify("Error adding permissions", { type: "error" });
    }
  };

  if (!subjectId) return null;

  return (
    <>
      <Button type="button" onClick={handleOpen}>
        <Plus className="h-4 w-4 mr-1" />
        Add permission
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => (o ? setOpen(true) : handleClose())}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add permissions</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div>
              <Select
                value={selectedServerId}
                onValueChange={setSelectedServerId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a resource server" />
                </SelectTrigger>
                <SelectContent>
                  {resourceServers.map((s) => (
                    <SelectItem key={s.identifier} value={s.identifier}>
                      {s.name || s.identifier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {withOrgScope && orgsLoadFailed && (
              <p className="text-sm text-destructive">
                Couldn&apos;t load organizations. Retry before adding a
                permission — otherwise it may be granted tenant-wide instead of
                scoped to an organization.
              </p>
            )}
            {withOrgScope && organizations.length > 0 && (
              <div>
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Organization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ORG}>
                      No organization (tenant-wide)
                    </SelectItem>
                    {organizations.map((o) => (
                      <SelectItem key={o.id} value={String(o.id)}>
                        {o.display_name || o.name || String(o.id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {selectedServerId && (
              <SelectionList
                key={`${selectedServerId}:${selectedOrgId}`}
                items={available.map((p) => ({
                  key: p.permission_name,
                  primary: p.permission_name,
                  secondary: p.description,
                  searchText: [p.permission_name, p.description].join(" "),
                }))}
                selected={selected}
                onToggle={toggle}
                loading={loading}
                emptyMessage={`This ${subjectNoun} already has all available scopes.`}
                searchPlaceholder="Search permissions"
                idPrefix="perm"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={selected.size === 0 || (withOrgScope && orgsLoadFailed)}
            >
              Add {selected.size > 0 ? `${selected.size} ` : ""}permission(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RemovePermissionCell({ resource }: { resource: "roles" | "users" }) {
  const record = useRecordContext<PermissionRecord>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { id: subjectId } = useParams();
  const [open, setOpen] = useState(false);

  if (!record) return null;

  const handleRemove = async () => {
    if (!subjectId) return;
    try {
      const id = encodeURIComponent(
        `${record.resource_server_identifier}:${record.permission_name}`,
      );
      await dataProvider.delete(`${resource}/${subjectId}/permissions`, {
        id,
        previousData: record,
      });
      notify("Permission removed", { type: "success" });
      setOpen(false);
      refresh();
    } catch {
      notify("Error removing permission", { type: "error" });
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Remove permission"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove permission</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Remove permission &quot;{record.permission_name}&quot; from &quot;
            {record.resource_server_name ?? record.resource_server_identifier}
            &quot;? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemove}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AssignedCell() {
  const record = useRecordContext<PermissionRecord>();
  if (!record?.created_at) return <>-</>;
  return <DateAgo date={record.created_at} />;
}

function OrganizationCell() {
  const record = useRecordContext<PermissionRecord>();
  const orgId = record?.organization_id;
  const { data } = useGetOne<Organization>(
    "organizations",
    { id: orgId as string },
    { enabled: !!orgId },
  );
  if (!orgId) {
    return <span className="text-muted-foreground">Tenant-wide</span>;
  }
  return <>{data?.display_name || data?.name || orgId}</>;
}

const SearchFormComponent = (props: FormHTMLAttributes<HTMLFormElement>) => (
  <form {...props} className="flex w-full" />
);

/**
 * Mirrors the toolbar rendered by <List> (search + filters + columns), which a
 * bare <ReferenceManyField> does not provide. Rendered inside the field so it
 * shares its ListContext (search) and resource context (columns store key).
 */
function PermissionsToolbar() {
  return (
    <div className="flex items-center gap-2 my-2">
      <div className="flex w-full max-w-sm">
        <FilterLiveForm formComponent={SearchFormComponent}>
          <SearchInput source="q" className="flex-grow" />
        </FilterLiveForm>
      </div>
      <Button
        type="button"
        variant="outline"
        disabled
        className="cursor-not-allowed opacity-60"
        title="No filters configured"
      >
        <Filter className="h-4 w-4" />
        Add filter
      </Button>
      <div className="ml-auto">
        <ColumnsButton />
      </div>
    </div>
  );
}

export function PermissionsTab({
  resource,
  target,
  subjectNoun,
  withOrgScope,
}: PermissionsTabProps) {
  const record = useRecordContext<{ id?: string }>();
  if (!record?.id) return null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <AddPermissionButton
          resource={resource}
          target={target}
          subjectNoun={subjectNoun}
          withOrgScope={withOrgScope}
        />
      </div>
      <ReferenceManyField
        reference="permissions"
        target={target}
        sort={{ field: "permission_name", order: "ASC" }}
        pagination={<ListPagination />}
        empty={
          <p className="text-sm text-muted-foreground py-4">
            No permissions assigned
          </p>
        }
      >
        <PermissionsToolbar />
        <DataTable rowClick={false} bulkActionButtons={false}>
          <DataTable.Col
            source="resource_server_identifier"
            label="Resource server"
          />
          <DataTable.Col source="resource_server_name" label="Resource name" />
          <DataTable.Col source="permission_name" label="Permission" />
          {withOrgScope && (
            <DataTable.Col label="Organization">
              <OrganizationCell />
            </DataTable.Col>
          )}
          <DataTable.Col label="Description">
            <TextField source="description" />
          </DataTable.Col>
          <DataTable.Col label="Assigned">
            <AssignedCell />
          </DataTable.Col>
          <DataTable.Col label="">
            <RemovePermissionCell resource={resource} />
          </DataTable.Col>
        </DataTable>
      </ReferenceManyField>
    </div>
  );
}
